import React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Popover,
  Button,
  Grid,
  FormHelperText,
} from "@mui/material";
import { format, parse, isValid, isPast, startOfDay } from "date-fns";

// Helper function to safely convert various input types to expected string format
const normalizeValueToString = (value) => {
  // Handle null, undefined, or empty values
  if (!value || value === '') {
    return '';
  }
  
  // If already a string, return as is
  if (typeof value === 'string') {
    return value;
  }
  
  // If it's a Date object, format it to expected string format
  if (value instanceof Date && isValid(value)) {
    return format(value, 'MM-dd-yy') + ' @ 08:00';
  }
  
  // If it's a number (timestamp), convert to Date first
  if (typeof value === 'number') {
    const date = new Date(value);
    if (isValid(date)) {
      return format(date, 'MM-dd-yy') + ' @ 08:00';
    }
  }
  
  // For any other type, return empty string
  console.warn('DateTimePicker: Unexpected value type received:', typeof value, value);
  return '';
};

// Helper function to safely extract date and time parts from string
const extractDateTimeParts = (valueString) => {
  if (!valueString || typeof valueString !== 'string') {
    return { datePart: '', timePart: '08:00' };
  }
  
  const parts = valueString.split(' @ ');
  return {
    datePart: parts[0] || '',
    timePart: parts[1] || '08:00'
  };
};

const DateTimePicker = ({
  value,
  onChange,
  label,
  required = false,
  placeholder = "MM-DD-YY @ HH:MM",
  name,
  size = "small",
  sx = {},
  errorMessage,
  error,
  disablePast,
  shouldDisableDate,
  disabled = false,
}) => {
  const isDateDisabled = (date) => {
    if (disablePast && isPast(startOfDay(date))) return true;
    if (shouldDisableDate && shouldDisableDate(date)) return true;
    return false;
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const inputRef = useRef(null);

  // Time options from 8:00 AM to 8:45 PM in 15-minute increments
  const timeOptions = Array.from({ length: 52 }, (_, i) => {
    const hour = Math.floor(i / 4) + 8;
    const minute = (i % 4) * 15;
    return `${hour.toString().padStart(2, "0")}:${minute
      .toString()
      .padStart(2, "0")}`;
  });

  useEffect(() => {
    if (value) {
      try {
        // Normalize the value to a safe string format
        const normalizedValue = normalizeValueToString(value);
        if (normalizedValue) {
          const { datePart } = extractDateTimeParts(normalizedValue);
          if (datePart) {
            const parsedDate = parse(datePart, "MM-dd-yy", new Date());
            if (isValid(parsedDate)) {
              setSelectedDate(parsedDate);
              setCurrentMonth(parsedDate);
            }
          }
        }
      } catch (error) {
        console.error("Invalid date format", error);
      }
    } else {
      // Reset state when value is cleared
      setSelectedDate(null);
    }
  }, [value]);

  const handleClick = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    const formattedDate = format(date, "MM-dd-yy");
    // Safely extract time part using helper function
    const normalizedValue = normalizeValueToString(value);
    const { timePart } = extractDateTimeParts(normalizedValue);
    onChange(`${formattedDate} @ ${timePart}`);
  };

  const handleTimeSelect = (time) => {
    const datePart = selectedDate
      ? format(selectedDate, "MM-dd-yy")
      : format(new Date(), "MM-dd-yy");
    onChange(`${datePart} @ ${time}`);
    handleClose();
  };

  const handlePrevMonth = () => {
    const prevMonth = new Date(currentMonth);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    setCurrentMonth(prevMonth);
  };

  const handleNextMonth = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    setCurrentMonth(nextMonth);
  };

  const handleClear = () => {
    onChange("");
    setSelectedDate(null);
    handleClose();
  };

  // Generate calendar days
  const generateCalendar = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.

    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);
    const totalDays = lastDay.getDate();

    // Generate calendar grid
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let i = 1; i <= totalDays; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const calendar = generateCalendar();
  const open = Boolean(anchorEl);
  const id = open ? "date-time-picker-popover" : undefined;

  return (
    <Box sx={{ position: "relative", ...sx }}>
      {label && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
            {label} {required && <span style={{ color: "red" }}>*</span>}
          </Typography>
        </Box>
      )}

      <TextField
        fullWidth
        sx={{
          backgroundColor: "white",
        }}
        variant="outlined"
        name={name}
        value={normalizeValueToString(value)}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        size={size}
        onClick={handleClick}
        inputRef={inputRef}
        disabled={disabled}
        InputProps={{
          readOnly: true,
        }}
        error={error}
      />
      {errorMessage && (
        <FormHelperText error={true}>{errorMessage}</FormHelperText>
      )}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ mt: 1 }}
      >
        <Box sx={{ display: "flex", width: "500px" }}>
          {/* Calendar Section */}
          <Box sx={{ p: 2, flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Button onClick={handlePrevMonth}>&lt;</Button>
              <Typography variant="subtitle1">
                {format(currentMonth, "MMMM yyyy")}
              </Typography>
              <Button onClick={handleNextMonth}>&gt;</Button>
            </Box>

            <Grid container spacing={0}>
              {/* Day headers */}
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <Grid item xs={1.7} key={day}>
                  <Box sx={{ textAlign: "center", py: 1 }}>
                    <Typography variant="caption">{day}</Typography>
                  </Box>
                </Grid>
              ))}

              {/* Calendar days */}
              {calendar.map((day, index) => (
                <Grid item xs={1.7} key={index}>
                  {day ? (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 1,
                        cursor: isDateDisabled(day) ? "not-allowed" : "pointer",
                        bgcolor:
                          selectedDate &&
                            day.getDate() === selectedDate.getDate() &&
                            day.getMonth() === selectedDate.getMonth() &&
                            day.getFullYear() === selectedDate.getFullYear()
                            ? "primary.light"
                            : "transparent",
                        "&:hover": {
                          bgcolor: isDateDisabled(day)
                            ? undefined
                            : "action.hover",
                        },
                        borderRadius: "4px",
                        color: isDateDisabled(day)
                          ? "text.disabled"
                          : "text.primary",
                      }}
                      onClick={() =>
                        !isDateDisabled(day) && handleDateSelect(day)
                      }
                    >
                      <Typography variant="body2">{day.getDate()}</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 1 }}></Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Time Section */}
          <Box
            sx={{
              p: 2,
              borderLeft: "1px solid #e0e0e0",
              width: "150px",
              maxHeight: "300px",
              overflow: "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Time
            </Typography>
            {timeOptions.map((time) => {
              const normalizedValue = normalizeValueToString(value);
              const { timePart } = extractDateTimeParts(normalizedValue);
              const isSelected = timePart === time;
              
              return (
                <Typography
                  key={time}
                  sx={{
                    py: 0.5,
                    px: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                    ...(isSelected && {
                      fontWeight: "bold",
                      color: "primary.main",
                    }),
                    borderRadius: "4px",
                  }}
                  onClick={() => handleTimeSelect(time)}
                >
                  {time}
                </Typography>
              );
            })}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid #e0e0e0",
          }}
        >
          <Button onClick={handleClear} color="primary">
            Clear
          </Button>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateTimePicker;
